import React, { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ITotalPlanned } from '../../../../../model/offer/IOfferItems';
import { Platforms } from '../../../../../model/platform/Platforms';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import {
    getLocalDateWithoutTimezoneTemp,
    getManageFilterIcons,
    hasAccessRights,
    truncateString,
} from '../../../../../shared/functions/Functions';
import { manageBrand } from '../../../../../store/brand/manageBrandSlice';
import { CalendarListViewHeader } from '../calendar/components/CalendarListViewHeader';
import { iInfluencersCardProps } from './dto/IManageCards';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { Pagination } from '../../../../../shared/components/Pagination';
import { IAllStates } from '../../../../../store/rootReducer';
import { useNavigate, useParams } from 'react-router-dom';
import BookingOfferModal from '../../book/offer/modal';
import { IParams } from '../../../../../model/shared/IParams';
import { AccessRightEnum } from '../../../../../enum/AccessRightEnum';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import ChildOfferModal from '../../book/child-offer/modal';
import { User } from '../../../../../model/user/User';
import { setSelectedUser } from '../../../../../store/brand/childOfferBrandSlice';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import Flag from '../../../../../shared/components/flag/Flag';

export const InfluencersCard = ({
    listOfInfluencers,
    influencersInfoData,
    platformDropdownList,
    filterParams,
    setFilterPlannerStatus,
    handleChangeParams,
    setNewEvent,
    setShowModal,
    updateFeedbackLoop,
    skipReporting,
    handlePagination,
    influecersPagination,
    totalPages,
    setTriggerUpdate,
}: iInfluencersCardProps) => {
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { showBookModal, showChildModal, modalInfo } = useSelector((state: IAllStates) => state.manageBrand);
    const { campaign } = useSelector((state: IAllStates) => state.selectedCampaign);
    const { isClient, company } = useSelector((state: IAllStates) => state.auth.user);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showSkipReportingModal, setShowSkipReportingModal] = useState(false);
    const statusesToDisplay = ['proposed', 'scheduled'];

    const goToMeasure = () => {
        navigate(`/brand/campaign/measure/${id}`);
    };

    const handleInfluencerClick = (offerId: number, user?: User, isLongTerm?: boolean) => {
        if (!isClient && hasAccessRights(AccessRightEnum.CAN_VIEW_BOOKING)) {
            if (isLongTerm) {
                dispatch(setSelectedUser(user));
                dispatch(manageBrand.actions.setShowChildModal(true));
            } else {
                dispatch(manageBrand.actions.setModalInfo({ offerId, userId: user?.id }));
                dispatch(manageBrand.actions.setShowBookModal(true));
            }
        } else {
            navigate(`${InternalRoutes.INFLUENCER_PUBLIC_PROFILE}/${user?.id}`);
        }
    };

    const closeModal = () => {
        dispatch(manageBrand.actions.setShowBookModal(false));
    };
    const closeChildOffer = () => {
        dispatch(manageBrand.actions.setShowChildModal(false));
    };

    return (
        <>
            <BookingOfferModal
                show={showBookModal}
                closeModal={closeModal}
                userId={modalInfo?.userId}
                offerId={modalInfo?.offerId}
            />
            <ChildOfferModal show={showChildModal} closeModal={closeChildOffer} setUpdateList={setTriggerUpdate} />
            <CardLayout>
                <ConfirmationModal
                    action={updateFeedbackLoop}
                    title={t('brand.campaign.manage.confirm_skip')}
                    htmlDescription={t('brand.campaign.manage.confirm_skip_text')}
                    show={showConfirmModal}
                    closeModal={() => setShowConfirmModal(false)}
                    description=""
                />
                <ConfirmationModal
                    action={skipReporting}
                    title={t('brand.campaign.manage.confirm_skip_reporting')}
                    htmlDescription={t('brand.campaign.manage.confirm_skip_reporting_text')}
                    show={showSkipReportingModal}
                    closeModal={() => setShowSkipReportingModal(false)}
                    description=""
                />

                <div className="text-dark font-weight-semi-bold">{t('brand.campaign.manage.filterByInfluencer')}:</div>
                <CalendarListViewHeader
                    setFilterPlannerStatus={setFilterPlannerStatus}
                    handleChangeParams={handleChangeParams}
                    filterParams={filterParams}
                    platform={platformDropdownList as Platforms[]}
                />
                {hasAccessRights(AccessRightEnum.CAN_VIEW_MEASURE) && !isClient && (
                    <div className="d-flex align-items-center mb-3">
                        {campaign?.isReviewed ? (
                            <Button
                                className="w-100"
                                onClick={() => setShowConfirmModal(true)}
                                variant="outline-primary"
                            >
                                {t('brand.campaign.manage.skip_approval')}
                            </Button>
                        ) : (
                            <Button className="w-100" onClick={goToMeasure} variant="outline-primary">
                                {t('brand.campaign.manage.to_reporting')}
                            </Button>
                        )}
                        <TooltipOverlay
                            placement="left"
                            className="ms-2"
                            content={t('brand.campaign.manage.info_skip_approval')}
                        >
                            <HiOutlineInformationCircle size={20} className="text-primary" />
                        </TooltipOverlay>
                    </div>
                )}
                {!isClient && !campaign?.isLongTerm && company?.canSkipReporting && !campaign?.canSkipReporting && (
                    <div className="d-flex align-items-center mb-3">
                        <Button
                            className="w-100"
                            onClick={() => setShowSkipReportingModal(true)}
                            variant="outline-primary"
                        >
                            {t('brand.campaign.manage.skip_reporting')}
                        </Button>
                        <TooltipOverlay
                            placement="left"
                            className="ms-2"
                            content={t('brand.campaign.manage.info_skip_reporting')}
                        >
                            <HiOutlineInformationCircle size={20} className="text-primary" />
                        </TooltipOverlay>
                    </div>
                )}
                <div className="bg-muted default-radius p-md-3 py-2">
                    <Row className="flex-xl-row">
                        <Col xs={9} className="pl-4">
                            <h5 className="text-primary mb-0">{influencersInfoData?.count}</h5>
                            <div className="my-2">{t('general.offers')}</div>
                        </Col>
                        {influencersInfoData &&
                            influencersInfoData.count >= 1 &&
                            statusesToDisplay?.map(status =>
                                getManageFilterIcons({
                                    status,
                                    t,
                                    totalPlanned: influencersInfoData?.totalPlanned as ITotalPlanned,
                                    showManageIcon: false,
                                })
                            )}
                    </Row>
                </div>
                <Row className="mt-3 flex-xl-row">
                    {listOfInfluencers?.map((influencer, index) => {
                        const truncatedName = truncateString(influencer.user?.displayName ?? '', 20);
                        const isTruncated = (influencer.user?.displayName?.length ?? 0) > 20;

                        const displayName = isTruncated ? (
                            <TooltipOverlay content={influencer.user?.displayName}>
                                <p className="mb-0">{truncatedName ?? '-'}</p>
                            </TooltipOverlay>
                        ) : (
                            <p className="mb-0">{influencer.user?.displayName ?? '-'}</p>
                        );

                        return (
                            <React.Fragment key={influencer?.id}>
                                <Col
                                    xs={8}
                                    onClick={() =>
                                        handleInfluencerClick(
                                            influencer?.id ?? 0,
                                            influencer?.user,
                                            influencer.isLongTerm
                                        )
                                    }
                                    className={`d-flex align-items-center ${index > 0 ? 'my-1' : 'mb-1'}`}
                                >
                                    <div
                                        className={`d-flex align-items-center cursor-pointer justify-content-start w-100`}
                                    >
                                        <CustomImageHandler
                                            altTag="User Profile"
                                            classes="small-rounded-logo w-40px"
                                            thumbnailPath={influencer?.user?.profilePhoto?.thumbnail}
                                            photoPath={influencer?.user?.profilePhoto?.thumbnail ?? null}
                                            name={influencer.user?.displayName}
                                        />
                                        <div className="ml-2">
                                            {displayName}
                                            {influencer?.isLongTerm && (
                                                <div className={'custom-badge long-term'}>
                                                    {t(`brand.campaign.briefing.overview_card.long_term`)}
                                                </div>
                                            )}
                                            {influencer?.isOnTop && (
                                                <div className={'custom-badge on-top'}>
                                                    {t(`brand.campaign.briefing.overview_card.on_top`)}
                                                </div>
                                            )}
                                            {influencer?.isAddonsOnly && (
                                                <div className={'custom-badge additional'}>
                                                    {t('influencer.offer_management.modal.additional')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={1} className="my-1 text-info d-flex align-items-center justify-content-center">
                                    <Flag width={33} className="rounded" code={influencer?.user?.contact?.country} />
                                </Col>
                                <Col xs={3} className="my-1 text-info d-flex align-items-center justify-content-center">
                                    {influencer?.totalPlanned?.created ?? 0} /{' '}
                                    {influencer?.totalPlanned?.totalNbPosts ?? 0}
                                </Col>
                            </React.Fragment>
                        );
                    })}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-end pb-lg-0 mb-lg-0">
                            <Pagination
                                classWrapper="py-3"
                                page={influecersPagination.page}
                                totalPages={totalPages}
                                handlePagination={handlePagination}
                            />
                        </div>
                    )}
                    <Col>
                        <Button
                            className="w-100 mt-3 white-space"
                            onClick={() => {
                                setNewEvent((prev: { postingDate: string }) => ({
                                    ...prev,
                                    postingDate: getLocalDateWithoutTimezoneTemp(new Date()),
                                }));
                                setShowModal(true);
                            }}
                        >
                            {t('brand.campaign.manage.addPosting')}
                        </Button>
                    </Col>
                </Row>
            </CardLayout>
        </>
    );
};
